import styles from "./Widget.module.css";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import Loader from "react-feather/dist/icons/loader";

const DEFAULT_POSITION = "bottomRight";

const DEBUG = process.env.NODE_ENV !== "production";
const IS_DEPLOYED = window.location.hostname.includes("adventurestore.me");

const BASE_URL = IS_DEPLOYED
	? `https://${window.location.hostname}`
	: DEBUG
	? `http://${window.location.hostname}:4001`
	: "https://adventurestore.me";

export default function Widget({ config, storyConfig, onChatClick }) {
	const [dialogOpen, toggleDialog] = useState(false);

	const fullConfig = Object.assign({}, config || {}, {
		story: storyConfig || null,
	});

	return (
		<Portal>
			<Dialog
				visible={dialogOpen}
				config={fullConfig}
				onClose={() => toggleDialog(false)}
			/>
			<Bubble
				open={dialogOpen}
				config={fullConfig}
				onClick={() => toggleDialog(!dialogOpen)}
			/>
		</Portal>
	);
}

function Portal({ children }) {
	return createPortal(children, document.body);
}

function Bubble({ open, config, onClick }) {
	const { bubble, position } = config;

	return (
		<div
			className={classnames(
				styles.bubble,
				bubble?.className,
				open ? styles.open : null,
				styles[position || DEFAULT_POSITION]
			)}
			onClick={onClick}
		>
			{open ? "CLOSE" : "HELP"}
		</div>
	);
}

function Dialog({ visible, config, onClose }) {
	const [loading, setLoading] = useState(true);
	const { position } = config;

	const handleFrameLoaded = useCallback(() => setLoading(false), []);

	return (
		<div
			className={classnames(
				styles.dialog,
				visible ? styles.visible : null,
				styles[position || DEFAULT_POSITION]
			)}
		>
			{loading ? <Spinner /> : null}
			<ContentsFrame config={config} onLoaded={handleFrameLoaded} />
		</div>
	);
}

function ContentsFrame({ config, onLoaded }) {
	const [frameSize, setFrameSize] = useState({});
	const frameRef = useRef();

	const frameUrl = `${BASE_URL}/widget`;

	useEffect(() => {
		function handleMessage(event) {
			if (event.origin !== BASE_URL) {
				return;
			}

			if (typeof event.data !== "object" || !event.data.widget) {
				return;
			}

			const data = event.data.widget;

			if (data.hello) {
				onLoaded && onLoaded();
				postMessageToFrame(frameRef, {
					config,
					context: { path: getCurrentPath() },
				});
			}
			if (data.height && data.height > 150) {
				setFrameSize({ height: data.height });
			}
		}
		window.addEventListener("message", handleMessage, false);

		return () =>
			window.removeEventListener("message", handleMessage, false);
	}, [config, onLoaded]);

	useEffect(() => {
		function handleLocationChange() {
			postMessageToFrame(frameRef, {
				context: { path: getCurrentPath() },
			});
		}

		window.addEventListener("popstate", handleLocationChange);
		return () =>
			window.removeEventListener("popstate", handleLocationChange);
	}, []);

	return (
		<iframe
			className={styles.frame}
			src={frameUrl}
			ref={frameRef}
			style={frameSize}
			title="Widget"
			allowFullScreen={false}
			allowpaymentrequest="true"
			loading="eager"
			referrerPolicy="origin-when-cross-origin"
			onLoad={onLoaded}
		/>
	);
}

function Spinner() {
	return (
		<div className={styles.spinner}>
			<Loader className={styles.spinner__icon} size={50} />
		</div>
	);
}

function postMessageToFrame(frameRef, data) {
	if (!frameRef.current?.contentWindow) {
		console.warn("No content window");
		return;
	}

	frameRef.current.contentWindow.postMessage({ widget: data }, BASE_URL);
}

function getCurrentPath() {
	return window.location.href;
}
